import React, { useState } from 'react';
import { PageLayout } from '../../shared/PageLayout';
import { Card } from '../../../components/ui/Card';
import { Settings, Edit2, School, Key, CheckCircle2, AlertCircle, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { SettingsSection } from '../../../components/ui/SettingsSection';
import { useAppStore } from '../../../app/hooks/useAppStore';
import { useAppNavigation } from '../../../app/hooks/useAppNavigation';
import type { Classroom, TeacherAccount } from '../../../accounts';
import { prepareAccountDeletion, executeAccountDeletion, type AccountDeletionDecision, type AccountDeletionPreparation } from '../../../accounts';
import { usePasswordChange } from '../../shared/usePasswordChange';
interface SettingsPageProps {
  activeAccount: TeacherAccount;
  classrooms?: Classroom[];
  onNavigate?: (route: string) => void;
}
export function SettingsPage({
  activeAccount,
  classrooms = [],
  onNavigate
}: SettingsPageProps) {
  // Singleton store — all consumers share the same state (Zustand refactor s81)
  const {
    accounts,
    clearActiveAccount,
    updateAccountNameById,
    updateAccountEmail,
    updateTeacherDetails,
    updatePassword
  } = useAppStore();
  // Profile editing state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState(activeAccount?.displayName || '');
  const [editEmail, setEditEmail] = useState(activeAccount?.email || '');
  const [editSchool, setEditSchool] = useState(activeAccount?.schoolName || '');
  // Password change — shared hook
  const pw = usePasswordChange({
    accountId: activeAccount.id,
    passwordHash: activeAccount.passwordHash,
    role: 'teacher',
    updatePassword
  });
  const nav = useAppNavigation({
    accounts,
    activeAccount,
    isInitialized: true,
    switchAccount: () => {},
    setViewingLearner: () => {},
    loadDemoAccounts: () => {},
    clearActiveAccount
  });
  // Account deletion state
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deletionPrep, setDeletionPrep] = useState<AccountDeletionPreparation | null>(null);
  const [deletionDecisions, setDeletionDecisions] = useState<Record<string, AccountDeletionDecision>>({});
  const handleStartDeletion = () => {
    const prep = prepareAccountDeletion(accounts, activeAccount.id);
    setDeletionPrep(prep);
    // Initialize decisions
    const initialDecisions: Record<string, AccountDeletionDecision> = {};
    prep.ownedClassrooms.forEach(c => {
      initialDecisions[c.classroom.id] = {
        classroomId: c.classroom.id,
        action: 'delete' // Default action
      };
    });
    setDeletionDecisions(initialDecisions);
    setIsDeletingAccount(true);
  };
  const handleConfirmDeletion = () => {
    if (!deletionPrep) return;
    // Ensure all classrooms have a valid decision
    const decisions = Object.values(deletionDecisions);
    const isValid = decisions.every(d => {
      if (d.action === 'transfer') return !!d.newTeacherId;
      return true;
    });
    if (!isValid) return;
    // Execute deletion
    executeAccountDeletion(accounts, activeAccount.id, decisions);
    // Clear active account and navigate to login
    clearActiveAccount();
    nav.navigateToLogin();
  };
  const handleSaveProfile = () => {
    if (editName.trim()) {
      updateAccountNameById(activeAccount.id, editName.trim());
    }
    if (editEmail.trim() !== (activeAccount.email || '')) {
      updateAccountEmail(activeAccount.id, editEmail.trim());
    }
    if (editSchool.trim() !== (activeAccount.schoolName || '')) {
      updateTeacherDetails(activeAccount.id, {
        schoolName: editSchool.trim()
      });
    }
    setIsEditingProfile(false);
  };
  return <PageLayout title="Instellingen" description="Beheer je docentaccount en voorkeuren" data-id="element-9017">
      <div className="max-w-3xl mx-auto space-y-6" data-id="element-9018">
        {/* Account Info */}
        <SettingsSection title="Account Instellingen" description="Beheer je persoonlijke gegevens" icon={Settings} accentColor="bg-violet-100 text-violet-600" action={!isEditingProfile ? <Button onClick={() => {
        setEditName(activeAccount?.displayName || '');
        setEditEmail(activeAccount?.email || '');
        setEditSchool(activeAccount?.schoolName || '');
        setIsEditingProfile(true);
      }} variant="ghost" size="sm" data-id="element-9020">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-9021" /> Bewerk
              </Button> : null} data-id="element-9019">
          <div className="space-y-6" data-id="element-9022">
            {isEditingProfile ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-9023">
                <div data-id="element-9024">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9025">
                    Naam
                  </label>
                  <Input variant="admin" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Jouw naam" autoFocus data-id="element-9026" />
                </div>
                <div data-id="element-9027">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9028">
                    E-mailadres
                  </label>
                  <Input variant="admin" type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} placeholder="jouw@email.nl" data-id="element-9029" />
                </div>
                <div className="sm:col-span-2" data-id="element-9030">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9031">
                    School
                  </label>
                  <Input variant="admin" value={editSchool} onChange={e => setEditSchool(e.target.value)} placeholder="Naam van je school" data-id="element-9032" />
                </div>
                <div className="sm:col-span-2 flex justify-end gap-2 mt-2" data-id="element-9033">
                  <Button onClick={() => setIsEditingProfile(false)} variant="secondary" data-id="element-9034">
                    Annuleren
                  </Button>
                  <button onClick={handleSaveProfile} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-9035">
                    Opslaan
                  </button>
                </div>
              </div> : <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-9036">
                  <div data-id="element-9037">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9038">
                      Naam
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-9039">
                      {activeAccount?.displayName}
                    </div>
                  </div>
                  <div data-id="element-9040">
                    <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9041">
                      E-mailadres
                    </label>
                    <div className="px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-slate-900 font-medium" data-id="element-9042">
                      {activeAccount?.email || <span className="text-slate-400 italic" data-id="element-9043">
                          Niet ingesteld
                        </span>}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-slate-100" data-id="element-9044">
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100 max-w-sm" data-id="element-9045">
                    <div className="p-2 bg-white rounded-lg border border-slate-200 text-slate-500 shadow-sm" data-id="element-9046">
                      <School className="w-5 h-5" data-id="element-9047" />
                    </div>
                    <div data-id="element-9048">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-0.5" data-id="element-9049">
                        School
                      </div>
                      <div className="font-medium text-slate-900" data-id="element-9050">
                        {activeAccount?.schoolName || 'Niet opgegeven'}
                      </div>
                    </div>
                  </div>
                </div>
              </>}
          </div>
        </SettingsSection>

        {/* Password Change */}
        <SettingsSection title="Wachtwoord" description="Wijzig je wachtwoord" icon={Key} accentColor="bg-violet-100 text-violet-600" action={!pw.isChangingPassword ? <Button onClick={() => pw.setIsChangingPassword(true)} variant="ghost" size="sm" data-id="element-9052">
                <Edit2 className="w-4 h-4 mr-2" data-id="element-9053" /> Wijzig
              </Button> : null} data-id="element-9051">
          {pw.isChangingPassword ? <form onSubmit={pw.handleChangePassword} className="space-y-4" data-id="element-9054">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-9055">
                <div className="sm:col-span-2" data-id="element-9056">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9057">
                    Huidig wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.currentPassword} onChange={e => pw.setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-9058" />
                </div>
                <div data-id="element-9059">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9060">
                    Nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.newPassword} onChange={e => pw.setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-9061" />
                </div>
                <div data-id="element-9062">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9063">
                    Bevestig nieuw wachtwoord
                  </label>
                  <Input variant="admin" type="password" value={pw.confirmPassword} onChange={e => pw.setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-9064" />
                </div>
              </div>

              {pw.passwordError && <div className="flex items-start gap-2 text-sm text-rose-700 bg-rose-50 p-3 rounded-lg border border-rose-100" data-id="element-9065">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-9066" />
                  <p data-id="element-9067">{pw.passwordError}</p>
                </div>}

              {pw.passwordSuccess && <div className="flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 p-3 rounded-lg border border-emerald-100" data-id="element-9068">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" data-id="element-9069" />
                  <p data-id="element-9070">{pw.passwordSuccess}</p>
                </div>}

              <div className="flex justify-end gap-2 pt-2" data-id="element-9071">
                <Button type="button" onClick={() => pw.resetPasswordForm()} variant="secondary" data-id="element-9072">
                  Annuleren
                </Button>
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold transition-all flex items-center gap-2" data-id="element-9073">
                  Wachtwoord Wijzigen
                </button>
              </div>
            </form> : <div className="text-sm text-slate-500" data-id="element-9074">
              Je wachtwoord is veilig opgeslagen. Klik op 'Wijzig' om een nieuw
              wachtwoord in te stellen.
            </div>}
        </SettingsSection>

        {/* Account Deletion */}
        <SettingsSection title="Account Verwijderen" description="Verwijder je account en beheer je klassen" icon={Trash2} accentColor="bg-rose-100 text-rose-600" data-id="element-9075">
          {!isDeletingAccount ? <div data-id="element-9076">
              <p className="text-sm text-slate-600 mb-4" data-id="element-9077">
                Als je je account verwijdert, verlies je toegang tot al je
                gegevens. Je kunt kiezen wat er met je klassen moet gebeuren.
              </p>
              <Button onClick={handleStartDeletion} variant="ghost" className="text-rose-600 hover:bg-rose-50 hover:text-rose-700" data-id="element-9078">
                Account verwijderen...
              </Button>
            </div> : <div className="space-y-6" data-id="element-9079">
              <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl" data-id="element-9080">
                <h4 className="font-bold text-rose-900 mb-2" data-id="element-9081">
                  Weet je zeker dat je je account wilt verwijderen?
                </h4>
                <p className="text-sm text-rose-800" data-id="element-9082">
                  Dit kan niet ongedaan worden gemaakt.
                  {deletionPrep && deletionPrep.ownedClassrooms.length > 0 && <span className="block mt-2 font-bold" data-id="element-9083">
                      Je beheert {deletionPrep.ownedClassrooms.length} klassen.
                      Maak hieronder een keuze per klas:
                    </span>}
                </p>
              </div>

              {deletionPrep && deletionPrep.ownedClassrooms.length > 0 && <div className="space-y-4" data-id="element-9084">
                  {deletionPrep.ownedClassrooms.map(c => {
              const decision = deletionDecisions[c.classroom.id];
              const hasTransferOptions = c.coTeachers.length > 0 || c.schoolColleagues.length > 0;
              return <div key={c.classroom.id} className="p-4 border border-slate-200 rounded-xl bg-white" data-id="element-9085">
                        <div className="flex items-center justify-between mb-3" data-id="element-9086">
                          <div className="font-bold text-slate-900" data-id="element-9087">
                            {c.classroom.name}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-9088">
                            {c.studentCount} leerlingen, {c.coTeachers.length}{' '}
                            co-docenten
                          </div>
                        </div>

                        <div className="space-y-2" data-id="element-9089">
                          {hasTransferOptions && <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-id="element-9090">
                              <input type="radio" name={`action-${c.classroom.id}`} checked={decision?.action === 'transfer'} onChange={() => setDeletionDecisions(prev => ({
                      ...prev,
                      [c.classroom.id]: {
                        ...prev[c.classroom.id],
                        action: 'transfer',
                        newTeacherId: c.coTeachers[0]?.id || c.schoolColleagues[0]?.id
                      }
                    }))} className="mt-1 text-indigo-600" data-id="element-9091" />
                              <div className="flex-1" data-id="element-9092">
                                <div className="text-sm font-medium text-slate-900" data-id="element-9093">
                                  Draag over aan:
                                </div>
                                {decision?.action === 'transfer' && <select value={decision.newTeacherId || ''} onChange={e => setDeletionDecisions(prev => ({
                        ...prev,
                        [c.classroom.id]: {
                          ...prev[c.classroom.id],
                          newTeacherId: e.target.value
                        }
                      }))} className="mt-2 w-full text-sm border-slate-200 rounded-lg" data-id="element-9094">
                                    {c.coTeachers.length > 0 && <optgroup label="Co-docenten" data-id="element-9095">
                                        {c.coTeachers.map(t => <option key={t.id} value={t.id} data-id="element-9096">
                                            {t.name}
                                          </option>)}
                                      </optgroup>}
                                    {c.schoolColleagues.length > 0 && <optgroup label="Collega's" data-id="element-9097">
                                        {c.schoolColleagues.map(t => <option key={t.id} value={t.id} data-id="element-9098">
                                            {t.name}
                                          </option>)}
                                      </optgroup>}
                                  </select>}
                              </div>
                            </label>}

                          <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-id="element-9099">
                            <input type="radio" name={`action-${c.classroom.id}`} checked={decision?.action === 'delete'} onChange={() => setDeletionDecisions(prev => ({
                      ...prev,
                      [c.classroom.id]: {
                        ...prev[c.classroom.id],
                        action: 'delete',
                        newTeacherId: undefined
                      }
                    }))} className="mt-1 text-indigo-600" data-id="element-9100" />
                            <div className="text-sm font-medium text-slate-900" data-id="element-9101">
                              Verwijder klas
                            </div>
                          </label>

                          <label className="flex items-start gap-3 p-2 rounded-lg hover:bg-slate-50 cursor-pointer" data-id="element-9102">
                            <input type="radio" name={`action-${c.classroom.id}`} checked={decision?.action === 'orphan'} onChange={() => setDeletionDecisions(prev => ({
                      ...prev,
                      [c.classroom.id]: {
                        ...prev[c.classroom.id],
                        action: 'orphan',
                        newTeacherId: undefined
                      }
                    }))} className="mt-1 text-indigo-600" data-id="element-9103" />
                            <div className="text-sm font-medium text-slate-900" data-id="element-9104">
                              Bewaar als verweesd (admin handelt af)
                            </div>
                          </label>
                        </div>
                      </div>;
            })}
                </div>}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-9105">
                <Button onClick={() => setIsDeletingAccount(false)} variant="secondary" data-id="element-9106">
                  Annuleren
                </Button>
                <Button onClick={handleConfirmDeletion} className="bg-rose-600 hover:bg-rose-700 text-white px-6 py-2 rounded-xl text-sm font-bold transition-all" disabled={deletionPrep && Object.values(deletionDecisions).some(d => d.action === 'transfer' && !d.newTeacherId)} data-id="element-9107">
                  Bevestig verwijdering
                </Button>
              </div>
            </div>}
        </SettingsSection>

        {/* Klas-beheer staat in een aparte pagina (Beheer) — geen duplicatie hier. */}
        <div className="text-xs text-slate-400 text-center pt-2" data-id="element-9108">
          Wil je klassen of leerlingen beheren?{' '}
          <button onClick={() => onNavigate?.('management')} className="text-indigo-600 hover:text-indigo-700 font-bold underline-offset-2 hover:underline" data-id="element-9109">
            Ga naar Beheer →
          </button>
        </div>
      </div>
    </PageLayout>;
}